import PageLayout from "@/components/PageLayout";

const LAST_UPDATED = "1 January 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10">
      <h2 className="text-lg font-extrabold text-slate-900 mb-3 pb-2 border-b border-slate-200">{title}</h2>
      <div className="space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Privacy() {
  const seoProps = {
    title: "Privacy Policy — How Sendora Protects Your Data",
    description: "Read Sendora's Privacy Policy. Learn how we collect, use and protect your personal data in compliance with NDPR and Nigerian data protection laws.",
    canonical: "/privacy",
    keywords: "Sendora privacy policy, data protection Nigeria, NDPR compliance, SMS platform privacy",
    schema: [{
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": "https://www.sendoras.com/privacy",
      "name": "Privacy Policy — Sendora",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sendoras.com" },
          { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": "https://www.sendoras.com/privacy" }
        ]
      }
    }]
  };
  return (
    <PageLayout title="Privacy Policy" subtitle={`Last updated: ${LAST_UPDATED}`} seo={seoProps}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-5 mb-10 text-sm text-[#0c1e41]">
          <strong>Summary:</strong> Sendora collects only the data necessary to provide our SMS platform service. We never sell your data to third parties. You can request deletion at any time by contacting <a href="mailto:privacy@sendora.ng" className="underline font-medium">privacy@sendora.ng</a>.
        </div>

        <Section id="information-we-collect" title="1. Information We Collect">
          <p><strong>Account Information:</strong> When you register, we collect your name, business name, email address, phone number, and password (stored as a one-way hash).</p>
          <p><strong>Billing Information:</strong> We collect payment references, bank transfer details, and transaction records. We do not store full card numbers — card payments are processed by Paystack, who maintain their own PCI-DSS compliance.</p>
          <p><strong>SMS Data:</strong> We store the content, recipient phone numbers, sender ID, delivery status, and timestamps of messages you send through our platform. This data is necessary to provide delivery reports and analytics.</p>
          <p><strong>Contact Lists:</strong> Phone numbers, names, and other data you upload to your contact lists are stored and used exclusively to send messages on your behalf.</p>
          <p><strong>Usage Data:</strong> We collect server logs, API request logs, and session data to secure the platform, detect abuse, and improve performance.</p>
        </Section>

        <Section id="how-we-use-data" title="2. How We Use Your Data">
          <p>We use your data exclusively to: (a) operate and improve the Sendora platform; (b) deliver SMS messages through our providers; (c) generate delivery reports and analytics for your account; (d) process payments and maintain billing records; (e) send service notifications, security alerts, and product updates; (f) comply with Nigerian regulatory requirements (NCC, NDPR).</p>
          <p>We do <strong>not</strong> use your contact lists for marketing purposes. Your recipients' data belongs to you.</p>
        </Section>

        <Section id="data-sharing" title="3. Data Sharing & Third Parties">
          <p><strong>SMS Providers:</strong> Recipient phone numbers and message content are transmitted to our SMS network providers (e.g. BulkSMSLive) to deliver your messages. These providers are contractually bound to use this data only for delivery purposes.</p>
          <p><strong>Payment Processors:</strong> Billing data is shared with Paystack and relevant banks to process payments. We never store raw card details.</p>
          <p><strong>Legal Compliance:</strong> We may disclose data when required by Nigerian law, court order, or to protect the rights and safety of Sendora, our users, or the public.</p>
          <p>We do <strong>not</strong> sell, rent, or trade your personal data to advertisers or data brokers.</p>
        </Section>

        <Section id="data-retention" title="4. Data Retention">
          <p>We retain account data for the lifetime of your account plus 90 days after deletion. SMS records and billing records are retained for 7 years to comply with Nigerian financial regulations. You may request earlier deletion of personal data not subject to regulatory retention obligations.</p>
        </Section>

        <Section id="security" title="5. Security">
          <p>We implement industry-standard security measures including TLS 1.3 encryption in transit, bcrypt password hashing, JWT-based session management, brute-force login protection, and regular security audits. Access to production data is restricted to authorised personnel only.</p>
          <p>Despite these measures, no system is 100% secure. If you discover a security vulnerability, please report it responsibly to <a href="mailto:security@sendora.ng" className="text-[#0c1e41] underline font-medium">security@sendora.ng</a>.</p>
        </Section>

        <Section id="your-rights" title="6. Your Rights (NDPR & GDPR)">
          <p>Under the Nigeria Data Protection Regulation (NDPR) and applicable law, you have the right to: (a) access the personal data we hold about you; (b) correct inaccurate data; (c) request deletion of your data; (d) object to or restrict processing; (e) data portability (export your contacts and message history).</p>
          <p>To exercise these rights, email <a href="mailto:privacy@sendora.ng" className="text-[#0c1e41] underline font-medium">privacy@sendora.ng</a> with the subject "Data Request". We will respond within 30 days.</p>
        </Section>

        <Section id="cookies" title="7. Cookies">
          <p>Sendora uses strictly necessary session cookies to maintain your login state. We do not use advertising cookies or third-party tracking pixels. You can disable cookies in your browser, but this will prevent you from logging in.</p>
        </Section>

        <Section id="changes" title="8. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. When we make material changes, we will notify you via email and display a notice on your dashboard at least 14 days before the changes take effect. Continued use of Sendora after the effective date constitutes acceptance of the updated policy.</p>
        </Section>

        <Section id="contact" title="9. Contact Us">
          <p>For privacy-related enquiries, contact our Data Protection Officer at <a href="mailto:privacy@sendora.ng" className="text-[#0c1e41] underline font-medium">privacy@sendora.ng</a></p>
        </Section>
      </div>
    </PageLayout>
  );
}
